import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { Col, Grid, Row, Stack, Text } from 'rsuite';
import { ExpressRegistrationRegistrant } from 'src/types/configuration';
import { ApplicationData, REGISTRANT_OPTIONS } from 'src/types/flow';
import { OwnersForm, OwnersFormRef } from '../Owners';
import {
  PropertyManagerForm,
  type PropertyManagerFormRef,
} from './PropertyManagerForm';
import FormFieldErrorIcon from '../FormFieldErrorIcon';
import { RegistrantRadioGroup } from './RegistrantRadioGroup';
import { mapFormModelToPerson } from 'src/common/people';
import { RegistrantFormRef, useRegistrantForm } from './useRegistrantForm';

export interface RegistrantFormProps {
  allowRoleChange?: boolean;
  data: ApplicationData;
  isAdminUpdate?: boolean;
  multipleOwners?: boolean;
  registrantOptions: ExpressRegistrationRegistrant;
}

export type { RegistrantFormRef };

export const RegistrantForm = forwardRef<
  RegistrantFormRef,
  RegistrantFormProps
>(({ allowRoleChange, multipleOwners, registrantOptions, ...rest }, ref) => {
  const ownersRef = useRef<OwnersFormRef>(null);
  const propertyManagerRef = useRef<PropertyManagerFormRef>(null);
  const {
    canChangeAllOwnerDetails,
    canChangePartialOwnerDetails,
    currentUser,
    effectiveOption,
    handleRoleChange,
    hasUnselectedOwnerRadioError,
    initialPropertyManager,
    owners,
    property,
    registrant,
    setHasUnselectedOwnerRadioError,
    setOwners,
    showOwnersForm,
    showPhysicalAddress,
    showPropertyManagerForm,
  } = useRegistrantForm({
    ...rest,
    allowRoleChange,
    multipleOwners,
    registrantOptions,
  });

  const currentUserEmails = useMemo(
    () => (currentUser?.email ? [currentUser.email] : []),
    [currentUser?.email]
  );

  useImperativeHandle(ref, () => ({
    getValues: () => {
      if (!allowRoleChange) {
        return { registrant };
      }
      if (effectiveOption === REGISTRANT_OPTIONS.OWNER) {
        return {
          registrant: {
            owner: [
              ...(currentUser ? [currentUser] : []),
              ...owners.map(mapFormModelToPerson),
            ],
            registrant: currentUser,
            registrantType: REGISTRANT_OPTIONS.OWNER,
          },
        };
      } else if (
        effectiveOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER ||
        effectiveOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
      ) {
        const propertyManagerValues = showPropertyManagerForm
          ? propertyManagerRef.current?.getValues() || currentUser
          : currentUser;
        return {
          registrant: {
            owner: owners.map(mapFormModelToPerson),
            registrant: propertyManagerValues,
            registrantType: effectiveOption,
          },
        };
      }
      return { registrant };
    },
    validateForm: () => {
      if (!effectiveOption) {
        setHasUnselectedOwnerRadioError(true);
        return false;
      }

      let isValid = true;
      if (effectiveOption === REGISTRANT_OPTIONS.OWNER && showOwnersForm) {
        isValid = ownersRef.current?.validateForm() ?? false;
      } else if (
        effectiveOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER ||
        effectiveOption === REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
      ) {
        const pmValid =
          !showPropertyManagerForm ||
          Boolean(propertyManagerRef.current?.validateForm());
        const ownersValid =
          !showOwnersForm || Boolean(ownersRef.current?.validateForm());
        isValid = [pmValid, ownersValid].every(Boolean);
      }

      if (!isValid) return false;

      return true;
    },
  }));

  return (
    <Grid
      className="mt-12 ml-0"
      fluid
    >
      <Row>
        <Col
          xs={24}
          md={12}
        >
          <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
            <legend>
              <Text>Are you the owner of the rental property?</Text>
            </legend>
            {hasUnselectedOwnerRadioError && (
              <FormFieldErrorIcon
                errorMessage={
                  'Select whether you are the property owner to continue'
                }
              />
            )}
            <RegistrantRadioGroup
              configuration={registrantOptions}
              disabled={!allowRoleChange}
              onChange={handleRoleChange}
              value={effectiveOption}
              ariaInvalid={hasUnselectedOwnerRadioError || undefined}
              ariaDescribedBy={
                hasUnselectedOwnerRadioError
                  ? 'registrant-radio-error'
                  : undefined
              }
            />
            {hasUnselectedOwnerRadioError && (
              <span
                id="registrant-radio-error"
                className="text-error"
              >
                Select whether you are the property owner to continue
              </span>
            )}
          </fieldset>
        </Col>
      </Row>

      {showPropertyManagerForm && (
        <Row>
          <Col
            xs={24}
            md={12}
          >
            <PropertyManagerForm
              ref={propertyManagerRef}
              hideCompanyName={!!currentUser?.companyName}
              initialValue={initialPropertyManager}
              isIndependent={
                effectiveOption ===
                REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT
              }
              title="Property Manager Details"
            />
          </Col>
        </Row>
      )}
      {showOwnersForm && (
        <OwnersForm
          ref={ownersRef}
          disabled={!!canChangeAllOwnerDetails}
          disableSimpleEdition={canChangePartialOwnerDetails}
          existingEmails={currentUserEmails}
          multipleOwners={multipleOwners}
          onChange={setOwners}
          propertyAddress={property?.propertyaddress}
          showPhysicalAddress={showPhysicalAddress}
          values={owners}
        />
      )}
    </Grid>
  );
});

RegistrantForm.displayName = 'RegistrantForm';
